import api from './api';

// Define interface for consignee with engineer assignment
export interface ConsigneeReportCard {
  title: string;
  itemName: string;
  quantity: number;
  visibleFields: Record<string, boolean>;
  itemGroups: Array<{
    fieldValues: Record<string, string>;
  }>;
}

export interface Consignee {
  name: string;
  designation: string;
  number: string;

  // Engineer assignment for this consignee
  assignedEngineerId?: string;
  assignedEngineerName?: string;
  engineerAssignedAt?: string | null;
  engineerNotes?: string;
  engineerStatus?: 'assigned' | 'site-visit' | 'design' | 'execution' | 'completed';
  engineerStartDate?: string | null;
  engineerCompletionDate?: string | null;

  // Consignee contact details
  callAlignedDate?: string | null;
  emailId?: string;
  faxNumber?: string;

  // Report templates for this consignee
  reportCards?: ConsigneeReportCard[];
}

export interface TenderReportData {
  id?: string;
  tenderId: string;
  selectedTender?: string;
  t247Id?: string;
  selectedCompanyProfile?: string;
  companyProfileName?: string;
  referenceNumber?: string;
  purchaseOrderNumber?: string;
  reportDate?: string | null;
  poDate?: string | null;
  customerName?: string;
  customerAddress?: string;
  stateOffice?: string;
  contactPersonName?: string;
  contactPersonDesignation?: string;
  contactPersonNumber?: string;
  department?: string;

  // NEW: Multiple consignees with their own engineer assignments and report cards
  consignees?: Consignee[];

  // Legacy fields - keeping for backward compatibility
  consigneeName?: string;
  consigneeDesignation?: string;
  consigneeNumber?: string;
  callAlignedDate?: string | null;
  emailId?: string;
  faxNumber?: string;
  itemName?: string;
  makeAndModel?: string[];
  monitorSerialNumber?: string[];
  cpuSerialNumber?: string[];
  serialNumberCommon?: string[];
  installationStatus?: string[];
  remarks?: string[];
  visibleFieldsConfig?: Record<string, boolean>;
  reportTemplateData?: Record<string, unknown>;
  reportTitle?: string;
  createdBy?: string;
  updatedBy?: string;
}

class TenderReportService {
  private baseUrl = '/api/tender-reports';

  // Save report (will create or update based on tenderId)
  async saveReport(data: TenderReportData): Promise<TenderReportData> {
    try {
      console.log('📤 TenderReportService.saveReport called with:', JSON.stringify(data, null, 2))

      // Ensure consignees is always an array
      const processedData = {
        ...data,
        consignees: data.consignees || []
      }

      const response = await api.post(this.baseUrl, processedData)
      console.log('📥 TenderReportService.saveReport response:', response.data)
      return response.data.data
    } catch (error) {
      console.error('❌ TenderReportService.saveReport error:', error)
      throw error
    }
  }

  // Get all reports
  async getAllReports(): Promise<TenderReportData[]> {
    const response = await api.get(this.baseUrl);
    return response.data.data;
  }

  // Get report by ID
  async getReportById(id: string): Promise<TenderReportData> {
    const response = await api.get(`${this.baseUrl}/${id}`);
    return response.data.data;
  }

  // Get all engineer consignee assignments (for admin)
  async getAllEngineerConsignees(): Promise<TenderReportData> {
    const response = await api.get('/api/engineer-consignees');
    return response.data.data;
  }

  // Get consignees for a specific engineer (for engineer dashboard)
  async getEngineerConsignees(engineerId: string): Promise<TenderReportData> {
    const response = await api.get(`/api/engineer/${engineerId}/consignees`);
    return response.data.data;
  }

  // Get report by tender ID
  async getReportByTenderId(tenderId: string): Promise<TenderReportData | null> {
    try {
      const response = await api.get(`${this.baseUrl}/tender/${tenderId}`);

      // Ensure consignees is always an array in the response
      if (response.data.data && !response.data.data.consignees) {
        // If no consignees array, create from legacy fields
        const data = response.data.data;
        if (data.consigneeName || data.consigneeDesignation || data.consigneeNumber) {
          data.consignees = [{
            name: data.consigneeName || '',
            designation: data.consigneeDesignation || '',
            number: data.consigneeNumber || '',
            callAlignedDate: data.callAlignedDate,
            emailId: data.emailId,
            faxNumber: data.faxNumber,
            reportCards: data.reportTemplateData?.cards || []
          }];
        } else {
          data.consignees = [];
        }
      }

      return response.data.data;
    } catch (error: unknown) {
      if (error instanceof Object && 'response' in error) {
        const errorWithResponse = error as { response?: { status?: number } };
        if (errorWithResponse.response?.status === 404) {
          return null;
        }
      }
      throw error;
    }
  }

  // Delete report
  async deleteReport(id: string): Promise<void> {
    await api.delete(`${this.baseUrl}/${id}`);
  }

  // Helper method to prepare data for API (ensures proper format)
  prepareDataForAPI(data: TenderReportData): Record<string, unknown> {
    const preparedData: Record<string, unknown> = {
      ...data,
      consignees: data.consignees || []
    };

    // Remove legacy fields if consignees is present to avoid confusion
    if (data.consignees && data.consignees.length > 0) {
      delete preparedData.consigneeName;
      delete preparedData.consigneeDesignation;
      delete preparedData.consigneeNumber;
      delete preparedData.callAlignedDate;
      delete preparedData.emailId;
      delete preparedData.faxNumber;
    }

    return preparedData;
  }

  // Helper method to validate consignee data
  validateConsignees(consignees: Consignee[]): boolean {
    return consignees.every(consignee => {
      // Basic validation - name is required
      if (!consignee.name || consignee.name.trim() === '') {
        return false;
      }
      return true;
    });
  }
}

export const tenderReportService = new TenderReportService();
